/* eslint-disable */
import { Box } from '@mui/material';
import { Form, Spin } from 'antd';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import MainCard from 'components/MainCard';
import { toastConfig } from 'config';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';

import CardAccordion from './CustomComponents/CardAccordion';
import LeaseDetailsShowAll from './CustomComponents/LeaseDetailsShowAll';
import { stateUpdater, utcConverter } from './CustomComponents/LeaseUtils/helper';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import ActionButtons from './CustomComponents/cardSections/ActionButtons';
import AgreementFiles from './CustomComponents/cardSections/AgreementFiles';
import LeaseBulkUpload from './CustomComponents/LeaseBulkUpload2';
import GeneralLeaseInformation from './Components/TabItems/GeneralLeaseInformation';
import FinancialTermsConditions from './Components/TabItems/FinancialTermsConditions';
import IFRS16FieldsModifications from './Components/TabItems/IFRS16FieldsModifications';
import AccountingInformation from './CustomComponents/cardSections/AccountingInformation';
import LeasePageHeader from '../../console-solution/lease-schedule-v5/LeaseUtils/LeasePageHeader';
import LeaseHistory from './CustomComponents/LeaseUtils/History/LeaseHistory';
import SubmitModalWithDate from './CustomComponents/LeaseUtils/Modal/SubmitModalWithDate';
import LeaseSchedule from './CustomComponents/cardSections/LeaseSchedule';

dayjs.extend(utc);

const Revise = ({ screen = 'REVISE' }) => {
	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const [selectedTab, setSelectedTab] = useState(0);
	const leaseDetailsShowAllRef = useRef();

	const [form] = Form.useForm();
	const { state } = useLocation();
	const navigate = useNavigate();

	// States
	const [formState, setFormState] = useState({
		showFormSubmitModal: false,
		submitPayload: null,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed with these edits?',
		},
	});
	const [dataFullyLoaded, setDataFullyLoaded] = useState(false);
	const [selectHistory, setSelectHistory] = useState(null);

	// Handle form value changes to sync main fields with first row of changes
	const handleFormValuesChange = (changedValues, allValues) => {
		let needsUpdate = false;
		const currentChanges = form.getFieldValue('changes') || [];
		if (currentChanges.length === 0) return;

		const updatedChanges = [...currentChanges];

		if (changedValues && changedValues.hasOwnProperty('retirementTerm')) {
			updatedChanges[0] = {
				...updatedChanges[0],
				inputChangeRetirementTerm: changedValues.retirementTerm || '',
			};
			needsUpdate = true;
		}

		if (changedValues && changedValues.hasOwnProperty('initialDiscountRate')) {
			updatedChanges[0] = {
				...updatedChanges[0],
				inputChangeDiscountRate: parseFloat(changedValues.initialDiscountRate) || 0,
			};
			needsUpdate = true;
		}

		if (changedValues && changedValues.hasOwnProperty('retirementCost')) {
			updatedChanges[0] = {
				...updatedChanges[0],
				inputChangeRetirementCost: parseFloat(changedValues.retirementCost) || 0,
			};
			needsUpdate = true;
		}

		if (changedValues && changedValues.hasOwnProperty('retirementStartDate')) {
			updatedChanges[0] = {
				...updatedChanges[0],
				inputChangeEffectiveDate: changedValues.retirementStartDate,
			};
			needsUpdate = true;
		}

		if (needsUpdate) {
			form.setFieldValue('changes', updatedChanges);
		}
	};

	// Simplified state setter
	const updateFormState = stateUpdater(setFormState);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	// API Hooks
	const [getSingleLeaseData, { data: singleLeaseData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();

	const [updateApi, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] =
		useGetSingleIdentityMutation();

	const [getLeaseData, { data: leaseControlData, isLoading: isleaseControlDataLoading }] = useSendControlDataMutation();

	// Computed values
	const statusData = useMemo(() => {
		return Object.keys(singleLeaseData?.parkedData ?? {})?.length === 0 ? singleLeaseData?.data : singleLeaseData?.parkedData;
	}, [singleLeaseData]);

	// Fetch the data when component mounts
	useEffect(() => {
		setDataFullyLoaded(false);

		getSingleLeaseData({
			suffixCode: '',
			_id: state?._id ?? '',
			operation: 'get-single',
			processCode: state?.processCode,
		});
	}, []);

	// Handle API responses and navigation
	useEffect(() => {
		if (isUpdateSuccess) {
			toast.success('Success! Record revised successfully', toastConfig);
			navigate(-1);
		}
		if (isUpdateError) {
			if (updateError?.data?.code === '501') {
				toast.error(updateError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Revision not completed', toastConfig);
			}
		}
	}, [isUpdateSuccess, updateError, isUpdateError]);

	// Set form values when data loads
	useEffect(() => {
		if (!statusData || Object.keys(statusData).length === 0) return;

		// Map form values from API data
		const viewValues = {
			// General Lease Information section
			tabIndex: '1',
			// modificationSequence: statusData?.modificationSequence,
			// retirementId: statusData?.retirementId,
			// retirementType: statusData?.retirementType,
			// retirementTitle: statusData?.retirementTitle,
			// lessorName: statusData?.lessorName,
			// paymentFrequency: statusData?.paymentFrequency,
			// paymentModality: statusData?.paymentModality,
			// creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
			// lastModifiedDate: dayjs(statusData?.lastModifiedDate),

			// Financial Terms & Conditions section
			// advanceAmount: statusData?.advanceAmount,
			// advanceDuration: statusData?.advanceDuration,
			// grossRent: statusData?.grossRent,
			retirementTerm: statusData?.retirementTerm,
			retirementStartDate: dayjs(statusData?.retirementStartDate),
			// leaseEndDate: dayjs(statusData?.leaseEndDate),
			// escalationPercentage: statusData?.escalationPercentage || 0,
			initialDiscountRate: statusData?.initialDiscountRate,
			// upfrontLeasePayment: statusData?.upfrontLeasePayment,
			// initialDirectCost: statusData?.initialDirectCost,
			// securityDeposit: statusData?.securityDeposit,
			retirementCost: statusData?.retirementCost,
			// endOfFinancialYear: dayjs(statusData?.endOfFinancialYear),

			// Other data
			// version: String(statusData?.version),
			// leaseLiability: statusData?.leaseLiability,
			// leaseName: statusData?.leaseName,
			// month: statusData?.month,
			// agreementFiles: statusData?.agreementFiles,
			// discountRateChanges: statusData?.discountRateChanges,
			// advanceAmounts: statusData?.advanceAmounts,
			// grossRents: statusData?.grossRents,
			// grossRentList: statusData?.grossRentList,
			// grossRentListSet: statusData?.grossRentListSet,
			// advanceAmountListSet: statusData?.advanceAmountListSet,
			// advanceAmountList: statusData?.advanceAmountList,

			// Changes
			changes: statusData?.changes?.map((item, index) => {
				const { inputChangeEffectiveDate, ...change } = item;
				return {
					// For first row, use retirementStartDate as effective date
					inputChangeEffectiveDate: index === 0 ? dayjs(statusData?.retirementStartDate) : dayjs(inputChangeEffectiveDate),
					inputChangeDescription: change.inputChangeDescription || 'Initial Retirement', // Keep for API compatibility
					inputChangeDiscountRate: change.inputChangeDiscountRate,
					inputChangeRetirementCost: change.inputChangeRetirementCost,
					inputChangeRetirementTerm: change.inputChangeRetirementTerm,
				};
			}),
		};

		// Set form values
		form.setFieldsValue(viewValues);

		// Mark data as fully loaded after a delay to ensure form is updated
		setTimeout(() => {
			setDataFullyLoaded(true);
		}, 500);
	}, [statusData]);

	// Handle data loading for calculations
	const handleDataLoading = async () => {
		if (!dataFullyLoaded) return;

		const formData = form.getFieldsValue();
		const effectiveChanges = formData.changes?.filter((_, index) => index !== 0);

		const payloadData = {
			advanceAmount: 0, // Default value for table calculations
			retirementTerm: formData?.changes?.[0]?.inputChangeRetirementTerm || formData?.retirementTerm,
			discountRate: formData?.changes?.[0]?.inputChangeDiscountRate || formData?.initialDiscountRate,
			// Keep these for API - derived from retirementStartDate
			startDay: String(formData?.retirementStartDate?.date()),
			startMonth: String(formData?.retirementStartDate?.month() + 1),
			startYear: String(formData?.retirementStartDate?.year()),
			escalation: 0, // Default value for table calculations
			paymentFrequency: 'Monthly', // Default value for table calculations
			paymentTime: 'Start', // Default value for table calculations
			advanceDuration: 0, // Default value for table calculations
			upfrontLeasePayment: 0, // Default value for table calculations
			initialDirectCost: 0, // Default value for table calculations
			securityDeposit: 0, // Default value for table calculations
			retirementCost: formData?.retirementCost,
			endOfFinancialYear: formData?.retirementStartDate, // Default value for table calculations
			advanceAmounts: [], // Default value for table calculations
			grossRents: [], // Default value for table calculations
			changes: effectiveChanges,
			grossRent: 0, // Default value for table calculations
		};

		const convertJson = JSON.stringify(payloadData);
		const convertBase64 = btoa(convertJson);
		const formattedPayload = {
			data: convertBase64,
			processCode: state?.processCode,
			scriptName: 'lease',
			payloadData: payloadData,
		};

		try {
			await getLeaseData(formattedPayload).unwrap();
		} catch (error) {
			console.error('Error in data loading:', error);
		}
	};

	// Track form data changes
	const hasFormDataChanged = () => {
		const currentFormData = form.getFieldsValue();

		const currentPayload = {
			retirementTerm: currentFormData?.retirementTerm,
			discountRate: currentFormData?.initialDiscountRate,
			retirementStartDate: currentFormData?.retirementStartDate?.format('YYYY-MM-DD'),
			retirementCost: currentFormData?.retirementCost,
		};

		// For initial load, always return true to trigger calculation
		return true;
	};

	// Submit the form
	const handleSubmit = async () => {
		try {
			await form.validateFields();
			const formData = form.getFieldsValue();

			// Prepare data for submission
			let grossRentListSet = formData?.grossRentListSet || [];
			let advanceAmountListSet = formData?.advanceAmountListSet || [];

			// grossRentListSet?.push(formData?.grossRentList);
			// advanceAmountListSet?.push(formData?.advanceAmountList);

			// Keep the existing changes structure, but update values
			const changesPayload = formData.changes?.map((change, index) => {
				// For the last row, include all updated fields
				if (index === formData.changes.length - 1) {
					return {
						...change,
						// inputChangeAdvance: formData?.advanceAmounts?.reduce((acc, curr) => acc + curr, 0) || 0,
						inputChangeEffectiveDate:
							index === 0 ? utcConverter(formData?.retirementStartDate) : utcConverter(change.inputChangeEffectiveDate),
						// upfrontLeasePayment: formData?.upfrontLeasePayment,
						// initialDirectCost: formData?.initialDirectCost,
						// securityDeposit: formData?.securityDeposit,
						inputChangeDiscountRate: formData?.initialDiscountRate,
						inputChangeRetirementCost: formData?.retirementCost,
						inputChangeRetirementTerm: formData?.retirementTerm,
						// inputChangeGrossRents: formData?.grossRents,
						// inputChangeEscalation: 0,
						// paymentFrequency: formData?.paymentFrequency,
						// paymentTime: formData?.paymentTime,
						// inputChangeAdvanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
						// inputChangeAdvanceAmounts: formData?.advanceAmounts,
						inputChangeDescription: change.inputChangeDescription || 'Initial Retirement', // Keep for API compatibility
					};
				}
				return {
					...change,
					inputChangeEffectiveDate: index === 0 ? utcConverter(formData?.retirementStartDate) : change.inputChangeEffectiveDate,
					inputChangeDescription: change.inputChangeDescription || 'Initial Retirement', // Keep for API compatibility
				};
			});

			// Create revision payload - keeping the structure but updating values
			const revisePayload = {
				retirementStartDate: utcConverter(formData?.retirementStartDate),
				// retirementTitle: formData?.retirementTitle,
				// lessorName: formData?.lessorName,
				retirementTerm: formData?.retirementTerm,
				// leaseEndDate: utcConverter(formData?.leaseEndDate),
				// paymentModality: formData?.paymentModality,
				// retirementType: formData?.retirementType,
				initialDiscountRate: formData?.initialDiscountRate,
				// grossRentList: formData?.grossRentList,
				// paymentFrequency: formData?.paymentFrequency,
				// advanceAmountList: formData?.advanceAmountList,
				// advanceAmount: formData?.advanceAmount,
				// advanceAmounts: formData?.advanceAmounts,
				// upfrontLeasePayment: formData?.upfrontLeasePayment,
				// initialDirectCost: formData?.initialDirectCost,
				// securityDeposit: formData?.securityDeposit,
				retirementCost: formData?.retirementCost,
				// grossRents: formData?.grossRents,
				// cashIncentiveAmount: formData?.cashIncentiveAmount,
				changes: changesPayload,
				// modifiedDateTime: utcConverter(dayjs()),
				// paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality)?.label,
				// lastModifiedDate: utcConverter(dayjs()),
				// grossRentListSet,
				// advanceAmountListSet,
				// advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
				// Keep these for API - derived from retirementStartDate
				startYear: formData?.retirementStartDate ? dayjs(formData?.retirementStartDate).year() : null,
				startMonth: formData?.retirementStartDate ? dayjs(formData?.retirementStartDate).month() + 1 : null,
				startDate: formData?.retirementStartDate ? dayjs(formData?.retirementStartDate).date() : null,
				inputChangeMonth: '1', // Default value for API
			};

			// Show confirm modal
			updateFormState('showFormSubmitModal', true);
			updateFormState('modalData.head', 'Confirmation');
			updateFormState('modalData.label', 'Do you want to revise this decommission asset record?');
			updateFormState('submitPayload', revisePayload);
		} catch (error) {
			console.error('Validation Failed:', error);
			toast.error('Please fill all required fields', toastConfig);
		}
	};

	// Handle modal submit
	const handleModalSubmit = (selectedDate) => {
		// Only proceed if we have a valid submitPayload with changes
		if (formState?.submitPayload?.changes && formState.submitPayload.changes.length > 0) {
			// Create a deep copy of the current submitPayload
			const updatedPayload = JSON.parse(JSON.stringify(formState.submitPayload));

			// Get the last change index
			const lastChangeIndex = updatedPayload.changes.length - 1;

			// Update the effective date
			updatedPayload.changes[lastChangeIndex].inputChangeEffectiveDate = utcConverter(selectedDate);

			// Calculate the length difference
			const advanceAmountLength = updatedPayload.changes[lastChangeIndex]?.inputChangeAdvanceAmounts?.length || 0;
			const grossRentLength = updatedPayload.changes[lastChangeIndex]?.inputChangeGrossRents?.length || 0;

			// Create zeros array if needed
			if (grossRentLength > advanceAmountLength) {
				const zerosArray = Array(grossRentLength - advanceAmountLength).fill(0);

				// Create a new array with the additional zeros
				updatedPayload.changes[lastChangeIndex].inputChangeAdvanceAmounts = [
					...(updatedPayload.changes[lastChangeIndex]?.inputChangeAdvanceAmounts || []),
					...zerosArray,
				];
			}

			// Update the state with the new payload using the setter
			updateFormState('submitPayload', updatedPayload);

			// After updating state, make the API call
			setTimeout(() => {
				updateApi({
					operation: 'update-single',
					processCode: state?.processCode,
					suffixCode: '',
					data: updatedPayload, // Use the updated payload directly
					_id: state._id,
					version: +singleLeaseData?.version,
				});
			}, 0);
		} else {
			// If no changes, just call the API with current state
			updateApi({
				operation: 'update-single',
				processCode: state?.processCode,
				suffixCode: '',
				data: formState?.submitPayload,
				_id: state._id,
				version: +singleLeaseData?.version,
			});
		}
	};
	useEffect(() => {
		if (dataFullyLoaded) {
			if (actionButtonsRef.current?.flag?.check !== 0) {
				// Check if form data has changed
				if (hasFormDataChanged()) {
					actionButtonsRef.current.handleDataLoading();
				}
			} else {
				actionButtonsRef.current.queryV3Loader();
			}
		}
	}, [dataFullyLoaded, screen]);
	// Tab handling
	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);

		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				leaseDetailsShowAllRef.current.loadData();
			}
		}
	};

	// Tab configuration
	const tabItem = [
		{
			key: '1',
			label: 'Retirement Details',
			children: (
				<>
					<CardAccordion
						title="Basic Information"
						darkTitle={true}
						sx={{ mb: 2 }}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<GeneralLeaseInformation screen={screen} />
					</CardAccordion>

					<FinancialTermsConditions screen={screen} dataFullyLoaded={dataFullyLoaded} />
					<ActionButtons
						screen={screen}
						viewData={singleLeaseData}
						selectHistory={setSelectHistory}
						dataFullyLoaded={dataFullyLoaded}
						ref={actionButtonsRef}
						handleUpdate={handleSubmit}
					/>
				</>
			),
		},
		{
			key: '2',
			label: 'Retirement Schedule',
			children: (
				<>
					<IFRS16FieldsModifications screen={screen} rowIndex={statusData?.changes?.length || 0} />
					<LeaseSchedule screen={screen} viewData={singleLeaseData} dataFullyLoaded={dataFullyLoaded} ref={leaseScheduleRef} />
				</>
			),
		},
		{
			key: '3',
			label: 'Modification History',
			children: <LeaseHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />,
		},
		{
			key: '4',
			label: 'Accounts Assignment',
			children: <AccountingInformation screen={screen} identityId={singleLeaseData?._id} />,
		},
		{
			key: '5',
			label: 'General Ledgers',
			children: (
				<CardAccordion
					title="Lease General Leasers View"
					darkTitle={true}
					sx={{ mt: 2, mb: 2 }}
					customHeaderSX={{
						backgroundColor: '#e1f0fb !important',
						p: '10px',
						fontSize: '16px !important',
					}}
					typographySX={{ fontSize: '16px !important' }}
				>
					<LeaseDetailsShowAll ref={leaseDetailsShowAllRef} />
				</CardAccordion>
			),
		},
		{
			key: '6',
			label: 'Agreements',
			children: (
				<CardAccordion
					title="Agreements"
					customHeaderSX={{
						backgroundColor: '#e1f0ff !important',
						p: '10px',
					}}
					typographySX={{ fontSize: '16px !important' }}
					darkTitle={true}
					sx={{ mb: 2 }}
				>
					<AgreementFiles screen={screen} />
				</CardAccordion>
			),
		},
		{
			key: '7',
			label: 'Bulk Upload',
			children: <></>, // Empty children since we render it outside the form
		},
	];

	return (
		<Box>
			<Spin spinning={isUpdateLoading || isViewLoading} style={{ maxHeight: '100%' }}>
				<LeasePageHeader
					title="Revise Decommission Asset"
					tabsItem={tabItem}
					selectedTab={selectedTab}
					selectTabHandler={selectTabHandler}
					createBtn={false}
				/>
				<MainCard sx={{ overflow: 'visible' }}>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						autoComplete="off"
						requiredMark={false}
						onValuesChange={handleFormValuesChange}
					>
						<AntdValuePreview form={form} show={false} />
						<Box sx={{ mt: 2 }}>
							{tabItem?.map((item, index) => (
								<Box
									key={index}
									style={{
										display: selectedTab === index ? 'block' : 'none',
									}}
								>
									{item.children}
								</Box>
							))}
						</Box>

						{formState?.showFormSubmitModal ? (
							form.getFieldsValue()?.modificationSequence === 0 ? (
								<ComptrollerConfirmModal
									showModal={formState?.showFormSubmitModal}
									modalData={formState?.modalData}
									setShowModal={setShowFormSubmitModal}
									handleSubmit={handleModalSubmit}
								/>
							) : (
								<SubmitModalWithDate
									showModal={formState?.showFormSubmitModal}
									modalData={formState?.modalData}
									setShowModal={setShowFormSubmitModal}
									handleSubmit={handleModalSubmit}
								/>
							)
						) : null}

						{selectHistory && (
							<IdentityCaseActivityModal
								title={state?.processName}
								setSelectHistory={setSelectHistory}
								selectItem={selectHistory}
								processCode={singleLeaseData?.processCode}
								processVersion={singleLeaseData?.parkProcessVersion}
							/>
						)}
					</Form>

					{/* Render Bulk Upload outside of form to prevent nested forms */}
					{selectedTab === 6 && (
						<Box sx={{ mt: 2 }}>
							<LeaseBulkUpload />
						</Box>
					)}
				</MainCard>
			</Spin>
		</Box>
	);
};

export default Revise;
